import { useEffect, useState } from "react"
import { DateRange } from "react-day-picker"
import { useTranslation } from "react-i18next"
import { CalendarBlank, CalendarCheck, CaretDown } from "@phosphor-icons/react"
import { addDays } from "date-fns"
import { useUser } from "@/apis/bff"
import { Button } from "@/components/ui/button"
import { Chip } from "@/components/ui/chip"
import { DateRangePicker } from "@/components/ui/date-range-picker"
import { formatDate, formatDateRange } from "@/lib/format-date"
import { cn } from "@/lib/utils"
import {
  useWaitingRoomFilters,
  useWaitingRoomFiltersContext,
} from "@/modules/waiting-room/contexts/waiting-room-filters-context"

const formatDateForFilter = (date: Date) => formatDate(date, "yyyy-MM-dd")

type AppointmentFilterProps = React.ComponentPropsWithoutRef<typeof Chip>

const DateRangeFilter = (props: AppointmentFilterProps) => {
  const { t } = useTranslation()
  const user = useUser()
  const { dateRange } = useWaitingRoomFilters().filters
  const setFilters = useWaitingRoomFiltersContext((state) => state.setFilters)
  const [selectedRange, setSelectedRange] = useState<DateRange>(
    dateRange ? { from: new Date(dateRange.from), to: new Date(dateRange.to) } : { from: new Date() },
  )

  const handleChange = (selectedRange: DateRange | undefined) => {
    setSelectedRange(selectedRange ?? { from: new Date() })

    if (selectedRange?.from && selectedRange?.to) {
      setFilters({
        dateRange: { from: formatDateForFilter(selectedRange.from), to: formatDateForFilter(selectedRange.to) },
      })
    }
  }

  const handleReset = () => {
    setSelectedRange({ from: new Date(), to: undefined })
    setFilters({ dateRange: null })
  }

  const handleNext14Days = () => {
    const today = new Date()
    const twoWeeksFromNow = addDays(today, 14)
    const newRange: DateRange = { from: today, to: twoWeeksFromNow }
    setSelectedRange(newRange)
    setFilters({
      dateRange: { from: formatDateForFilter(today), to: formatDateForFilter(twoWeeksFromNow) },
    })
  }

  const hasDateRange = !!selectedRange && !!selectedRange.from && !!selectedRange.to
  const CalendarIcon = hasDateRange ? CalendarCheck : CalendarBlank

  useEffect(() => {
    if (!dateRange) {
      setSelectedRange({ from: new Date(), to: undefined })
      return
    }
    setSelectedRange({ from: new Date(dateRange.from), to: new Date(dateRange.to) })
  }, [dateRange])

  return (
    <DateRangePicker
      value={selectedRange}
      onChange={handleChange}
      onReset={handleReset}
      shortcuts={
        user.isOneOf(["MedicalSupervisor"]) ? (
          <Button
            className="inline-flex h-8 items-center rounded-md text-sm outline-0"
            variant="secondary"
            data-testid="next-14-days-filter"
            onClick={handleNext14Days}>
            {t("scheduling.waitingRoom.filters.next14Days")}
          </Button>
        ) : undefined
      }>
      {({ selectedRange }) => (
        <Chip {...props} data-state={hasDateRange ? "checked" : "closed"} className="h-8">
          <div className={cn("flex items-center gap-0.5", hasDateRange && "text-on-primary-surface")}>
            <CalendarIcon size={20} />
            <div className="flex-1 truncate px-1.5">
              {!hasDateRange && t("scheduling.waitingRoom.filters.dateRange")}
              {hasDateRange && formatDateRange(selectedRange)}
            </div>
            <CaretDown size={20} />
          </div>
        </Chip>
      )}
    </DateRangePicker>
  )
}
DateRangeFilter.displayName = "DateRangeFilter"

export { DateRangeFilter }
